import { Outlet, useLocation } from "react-router-dom";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { AppErrorBoundary } from "../error-components/AppErrorFallback";
import Nav, { NavItem } from "../shared-components/Nav";
import {
  ClipboardListOutlineIcon,
  BuildingOutlineIcon,
  UserOutlineIcon,
  CandleOutlineIcon,
} from "../shared-components/icons";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { GuardedRoute } from "../shared-components/GuardedRoute";
import Skeleton from "../shared-components/Skeleton";
import Zendesk from "react-zendesk";
import { useUserCompanySettings } from "../users/UserCompanySettings";
import { useEffect, useMemo, useState } from "react";
import TagManager from "react-gtm-module-nonce-patch";
import { useAppContext } from "../AppProviders";
import {
  BellIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  DocumentCheckIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { CodeBracketSquareIcon } from "@heroicons/react/24/outline";
import { useGetHasTasksOnlyQueue } from "../api/UsersApi";
import I360Icon from "../shared-components/icons/360Icon";

const ZENDESK_KEY = process.env.REACT_APP_ZEND_DESK_KEY || "";

type LayoutProps = React.PropsWithChildren<{}>;

function buildNavigation(
  permissions: {
    [key: string]: boolean | { [key: string]: boolean };
  },
  hasOnlyQueue: boolean
) {
  const navigation: NavItem[] = [];
  if (hasOnlyQueue) {
    navigation.push({
      name: "Reports",
      href: "/reports/enter_queue",
      icon: ClipboardListOutlineIcon,
      current: true,
    });
  } else if (permissions.canManageReports) {
    navigation.push({
      name: "Reports",
      href: "/reports",
      icon: ClipboardListOutlineIcon,
      current: true,
    });
  }

  if (permissions.canView360) {
    navigation.push({
      name: "Reports",
      href: "/360",
      icon: I360Icon,
      current: true,
    });
  }

  if (permissions.canManageAdminView) {
    navigation.push({
      name: "Fama Admin View",
      href: "/admin",
      icon: TableCellsIcon,
      current: true,
    });
  }

  if (permissions.canManageCompanies)
    navigation.push({
      name: "Companies",
      href: "/companies",
      icon: BuildingOutlineIcon,
      current: true,
    });
  if (permissions.canManageUsers)
    navigation.push({
      name: "Users",
      href: "/users",
      icon: UserOutlineIcon,
      current: true,
    });
  if (permissions.canManageBlueprints)
    navigation.push({
      name: "Check Types",
      href: "/check-types",
      icon: ShieldCheckIcon,
      current: true,
    });
  if (permissions.canManageWorkflows)
    navigation.push({
      name: "Workflow",
      href: "/workflows",
      icon: CandleOutlineIcon,
      current: true,
    });

  if (permissions.canManageConsent)
    navigation.push({
      name: "Consent",
      href: "/consent-forms",
      icon: DocumentCheckIcon,
      current: true,
    });

  if (permissions.canManageAdminView)
    navigation.push({
      name: "Self Serve",
      href: "/self-serve",
      icon: DocumentArrowDownIcon,
      current: true,
    });

  if (permissions.canManageAdminView)
    navigation.push({
      name: "Prompt Builder",
      href: "/custom-behavior-prompts",
      icon: SparklesIcon,
      current: true,
    });

  if (permissions.canManageATS)
    navigation.push({
      name: "Integrations",
      href: "/ats",
      icon: CodeBracketSquareIcon,
      current: true,
    });

  if (permissions.canViewUsageReport)
    navigation.push({
      name: "Analytics",
      href: "/usage-report",
      icon: ChartBarIcon,
      current: true,
    });

  navigation.push({
    name: "Notification",
    href: "/notification",
    icon: BellIcon,
    current: true,
    disabled: true,
  });

  return navigation;
}

function Layout({ children }: LayoutProps) {
  const { user, roles, companiesFromAuth } = useAppContext();
  const location = useLocation();
  const inAddReport =
    location.pathname.includes("person/newmulti") ||
    location.pathname.includes("/reports-wizard");
  const permissions = usePermissionValidator();
  const [showChat, setShowChat] = useState(false);
  const userSettings = useUserCompanySettings();
  const { hasOnlyQueue } = useGetHasTasksOnlyQueue();

  const navMemo = useMemo(() => {
    return buildNavigation(permissions, hasOnlyQueue);
  }, [permissions, hasOnlyQueue]);

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: "ready",
        userId: user?.sub,
        email: user?.email,
        company: companiesFromAuth.allCompanies
          ? "all"
          : companiesFromAuth.companyIds
          ? companiesFromAuth.companyIds[0]
          : "na",
        roles: roles,
        userName: user?.nickname,
      },
    });
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setShowChat(userSettings?.showChatSupport || false);
  }, [userSettings]);

  return (
    <>
      <div>
        <div className="flex print:bg-white">
          {!inAddReport && <Nav navigation={navMemo ?? []}></Nav>}
          <main className="flex-1 col-span-3 h-svh">
            <div className="p-6 print:p-0 h-full">
              <div className="max-w-8xl mx-auto h-full">
                <GuardedRoute>
                  <Outlet />
                </GuardedRoute>
              </div>
            </div>
          </main>
        </div>
      </div>
      {showChat && (
        <Zendesk
          className="print:hidden"
          defer
          zendeskKey={ZENDESK_KEY}
          onLoaded={() => {}}
        />
      )}
    </>
  );
}

function LayoutWithErrorHandling({ children }: LayoutProps) {
  return (
    <AppErrorBoundary>
      <Layout>{children}</Layout>
    </AppErrorBoundary>
  );
}

export default withAuthenticationRequired(LayoutWithErrorHandling, {
  onRedirecting: () => <Skeleton />,
});
